import { useCallback, useEffect } from 'react';

import { TrackingCategory } from '@/entities/tracking/const';
import { TrackEventPayload } from '@/entities/tracking/lib/types';
import useTrackingProvider from '@/hooks/providers/useTrackingProvider';
import { ELEMENT_TYPES } from '@/utils/constants/tracking';
import { checkValidElement } from '@/utils/tracking/checkValidElement';
import { getElementType } from '@/utils/tracking/getElementType';
import { getTrackingInfo } from '@/utils/tracking/getTrackingInfo';
import { cleanEventInfo, getTrackingType } from '@/utils/tracking/getTrackingType';

const TRACKED_ELEMENT_SELECTOR =
    '[role="link"], [role="button"], a, button, [data-button], span[class^=vds-switch__root], [data-custom-track], [class*=vds-toggle]';

/**
 * Only elements with a defined event name (`data-prescribed-name` or `data-link-name`)
 * are tracked; names derived from text or URLs are dropped to keep event types bounded.
 */
const buildTrackEventPayload = (element: HTMLElement): TrackEventPayload | undefined => {
    const {
        text,
        trackingType: legacyTrackAction,
        filteredDataset,
        isPrescribedName,
        customTrackingName,
    } = getTrackingInfo(element, getElementType(element) as ELEMENT_TYPES);
    const prescribedName = isPrescribedName ? customTrackingName : undefined;

    if (!prescribedName && !filteredDataset.linkName && !filteredDataset.link_name) {
        return undefined;
    }

    return {
        type: prescribedName || getTrackingType(filteredDataset, text),
        detail: { context: cleanEventInfo({ ...filteredDataset }) },
        category: TrackingCategory.App.INTERACT,
        // dual support: the old action/name pair so the legacy
        // adobeDataLayer eventName stays what trackEventWithAppName produced
        legacyTrackAction,
        legacyTrackName: prescribedName || text,
    };
};

const useAutomaticTracking = () => {
    const { trackEvent } = useTrackingProvider();

    // NOTE:
    // Was previously listening to 'mousedown'. When a breakpoint/debugger paused inside
    // this handler, the user would often release the mouse while paused. That cancels the
    // subsequent natural 'click' event (because the release may occur off-target or the
    // gesture timing is broken), so React's onClick for the element never fired.
    // Listening to the actual 'click' event ensures we only track completed activations
    // and never interfere with the element's own onClick handler. It also filters out
    // drag / press-and-cancel cases automatically.
    const checkTracking = useCallback(
        (evt: MouseEvent) => {
            const target = evt.target as HTMLElement;
            if (!checkValidElement(target)) {
                return;
            }

            const trackedElement = target.closest<HTMLElement>(TRACKED_ELEMENT_SELECTOR);
            if (!trackedElement || 'preventAutoTracking' in trackedElement.dataset) {
                return;
            }

            const payload = buildTrackEventPayload(trackedElement);
            if (payload) {
                trackEvent(payload);
            }
        },
        [trackEvent],
    );

    useEffect(() => {
        document.addEventListener('click', checkTracking);
        return () => {
            document.removeEventListener('click', checkTracking);
        };
    }, [checkTracking]);
};

export default useAutomaticTracking;
